import { doc, serverTimestamp, Timestamp, writeBatch } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import {
  listLineSignals,
  markLineSignalsFlushed,
  recordLineSignalFlushFailure,
  type RawLineSignal,
} from './idbLineSignals'
import { isLineSignalCaptureEnabled } from './lineLearningSignals'
import { firebaseFunctions, firestoreDb } from './firebase'
import type { LineLearningWorkspacePolicy } from './lineWorkspacePolicy'

const MAX_BATCH_SIZE = 50
const IDLE_MS = 60_000

export type LineLearningFlushConfig = {
  sessionSalt: string
  policy: LineLearningWorkspacePolicy
}

type LineSignalDoc = {
  kind: RawLineSignal['kind']
  emittedAt: Timestamp
  ingestedAt: ReturnType<typeof serverTimestamp>
  axes: RawLineSignal['axes']
  sessionSalt: string
  clientVersion: string
  workspaceIdHash?: string
  /** Per-week salted identifier used only for abuse de-duplication. */
  turnIdHash: string
  aggregationState: 'PENDING'
}

export type FlushDependencies = {
  listSignals: () => Promise<RawLineSignal[]>
  markFlushed: (ids: string[]) => Promise<void>
  recordFailure: (ids: string[]) => Promise<void>
  fetchConfig: (workspaceId: string) => Promise<LineLearningFlushConfig>
  commit: (signals: RawLineSignal[], config: LineLearningFlushConfig, workspaceId: string) => Promise<void>
  now: () => number
  isCaptureEnabled: () => boolean
  isOnline: () => boolean
  saveDataEnabled: () => boolean
  visibilityState: () => DocumentVisibilityState | 'unknown'
}

export type FlushContext = {
  workspaceId: string
  lastActivityAt: number
}

let flushing = false
const connection = () => navigator as Navigator & { connection?: { saveData?: boolean } }

const defaultDependencies: FlushDependencies = {
  listSignals: listLineSignals,
  markFlushed: markLineSignalsFlushed,
  recordFailure: recordLineSignalFlushFailure,
  fetchConfig: async (workspaceId) => {
    const response = await httpsCallable<{ workspaceId: string }, LineLearningFlushConfig>(
      firebaseFunctions,
      'getLineLearningFlushConfig',
    )({ workspaceId })
    return response.data
  },
  commit: async (signals, config, workspaceId) => {
    const batch = writeBatch(firestoreDb)
    const workspaceIdHash = config.policy.anonymisationLevel === 'none'
      ? undefined
      : await hashWorkspaceId(workspaceId, config.sessionSalt)
    const turnIdHashes = await Promise.all(signals.map((signal) => hashWorkspaceId(signal.turnId, config.sessionSalt)))
    signals.forEach((signal, index) => {
      const payload: LineSignalDoc = {
        kind: signal.kind,
        emittedAt: Timestamp.fromMillis(signal.emittedAt),
        ingestedAt: serverTimestamp(),
        axes: signal.axes,
        sessionSalt: config.sessionSalt,
        clientVersion: (import.meta.env.VITE_CLIENT_VERSION as string | undefined) ?? 'unknown',
        turnIdHash: turnIdHashes[index],
        aggregationState: 'PENDING',
        ...(workspaceIdHash ? { workspaceIdHash } : {}),
      }
      batch.set(doc(firestoreDb, 'line_signals_raw', signal.id), payload)
    })
    await batch.commit()
  },
  now: Date.now,
  isCaptureEnabled: isLineSignalCaptureEnabled,
  isOnline: () => typeof navigator === 'undefined' || navigator.onLine !== false,
  saveDataEnabled: () => typeof navigator !== 'undefined' && connection().connection?.saveData === true,
  visibilityState: () => typeof document === 'undefined' ? 'unknown' : document.visibilityState,
}

export const canFlushLineLearningSignals = (
  context: Pick<FlushContext, 'lastActivityAt'>,
  dependencies: Pick<FlushDependencies, 'now' | 'isCaptureEnabled' | 'isOnline' | 'saveDataEnabled' | 'visibilityState'> = defaultDependencies,
) => {
  const idle = dependencies.visibilityState() === 'hidden' || dependencies.now() - context.lastActivityAt >= IDLE_MS
  return idle && dependencies.isCaptureEnabled() && dependencies.isOnline() && !dependencies.saveDataEnabled()
}

export const hashWorkspaceId = async (workspaceId: string, sessionSalt: string) => {
  if (typeof crypto === 'undefined' || !crypto.subtle) throw new Error('Web Crypto is unavailable for Line signal hashing.')
  const bytes = new TextEncoder().encode(`${workspaceId}:${sessionSalt}`)
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

export const flushLineLearningSignalsWithDependencies = async (
  context: FlushContext,
  dependencies: FlushDependencies,
) => {
  if (flushing || !context.workspaceId || !canFlushLineLearningSignals(context, dependencies)) return false
  flushing = true
  try {
    const signals = (await dependencies.listSignals()).filter((signal) => !signal.flushed).slice(0, MAX_BATCH_SIZE)
    if (signals.length === 0) return false
    let config: LineLearningFlushConfig
    try {
      config = await dependencies.fetchConfig(context.workspaceId)
    } catch {
      await dependencies.recordFailure(signals.map((signal) => signal.id))
      return false
    }
    if (!config.policy.signalCaptureEnabled || config.policy.globalKillSwitch) return false
    try {
      await dependencies.commit(signals, config, context.workspaceId)
      await dependencies.markFlushed(signals.map((signal) => signal.id))
      return true
    } catch {
      await dependencies.recordFailure(signals.map((signal) => signal.id))
      return false
    }
  } finally {
    flushing = false
  }
}

export const flushLineLearningSignals = (context: FlushContext) =>
  flushLineLearningSignalsWithDependencies(context, defaultDependencies)

/** Starts only passive triggers: hiding the document or a 60-second inactivity watchdog. */
export const startLineLearningSignalFlush = (workspaceId: string) => {
  if (typeof window === 'undefined' || !workspaceId) return () => undefined
  let lastActivityAt = Date.now()
  const flushIfAllowed = () => void flushLineLearningSignals({ workspaceId, lastActivityAt })
  const noteActivity = () => { lastActivityAt = Date.now() }
  const visibilityChange = () => {
    if (document.visibilityState === 'hidden') flushIfAllowed()
  }
  const watchdog = window.setInterval(flushIfAllowed, IDLE_MS)
  window.addEventListener('pointerdown', noteActivity, { passive: true })
  window.addEventListener('pointermove', noteActivity, { passive: true })
  window.addEventListener('keydown', noteActivity)
  document.addEventListener('visibilitychange', visibilityChange)
  document.addEventListener('pointerlockchange', flushIfAllowed)
  return () => {
    window.clearInterval(watchdog)
    window.removeEventListener('pointerdown', noteActivity)
    window.removeEventListener('pointermove', noteActivity)
    window.removeEventListener('keydown', noteActivity)
    document.removeEventListener('visibilitychange', visibilityChange)
    document.removeEventListener('pointerlockchange', flushIfAllowed)
  }
}

export const lineLearningSignalFlushInternals = {
  maxBatchSize: MAX_BATCH_SIZE,
  idleMs: IDLE_MS,
}
